<template>
  <div
    :class="{
      [extraClassWrapper]: extraClassWrapper !== '',
    }"
  >
    <picture>
      <source
        v-if="!errorRecreated && imgObj.srcsetWebp"
        :data-srcset="imgObj.srcsetWebp"
        :srcset="fetchpriority === 'high' ? imgObj.srcsetWebp : undefined"
        type="image/webp"
        class="lazyload"
      >
      <img
        ref="imgMain"
        :src="imgObj.preview"
        :data-src="imgObj.preview"
        :data-srcset="imgObj.srcsetWebp"
        :srcset="fetchpriority === 'high' ? imgObj.srcsetWebp : undefined"
        :sizes="imgObj.sizes"
        :title="imgObj.title"
        :alt="imgObj.alt"
        class="lazyload img-full"
        :fetchpriority="fetchpriority"
        :class="{
          blur: imgBlur === true,
          [extraClass]: extraClass !== '',
        }"
        :loading="loadingLazyAttribute ? loadingLazyAttribute : false"
        @error="imgLoadErr"
      >
    </picture>
  </div>
</template>

<script>
import 'lazysizes';
import ImageLoader from '../mixins/ImageLoader';

export default {
  name: 'LazyPhoto',
  mixins: [ImageLoader],
  i18nOptions: {
    namespaces: ['object'],
  },
  props: {
    imgSrc: {
      type: String,
      default: '',
    },
    imgNo: {
      type: Number,
      default: 1,
    },
    imgType: {
      type: String,
      default: 'objectDefault',
    },
    imgTitle: {
      type: String,
      default: '',
    },
    altOnly: {
      type: String,
      default: '',
    },
    imgBlur: {
      type: Boolean,
      default: false,
    },
    objectNumber: {
      type: [Number, String],
      default: '',
      required: false,
    },
    imgCdn: {
      type: String,
      default: '',
      required: false,
    },
    onlyPreview: {
      type: Boolean,
      default: false,
    },
    previewSize: {
      type: String,
      required: false,
      default: '250x155',
    },
    extraClass: {
      type: String,
      required: false,
      default: '',
    },
    extraClassWrapper: {
      type: String,
      required: false,
      default: '',
    },
    fetchpriority: {
      type: String,
      required: false,
      default: undefined
    },
    // set native loading="lazy" attribute for img tag
    // if empty, loading attribute will not be present on img tag
    // value can be "eager" or "lazy" (maybe more in the future)
    // see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/img#attr-loading
    loadingLazyAttribute: {
      type: [String, Boolean],
      default: '',
    },
  },
  data() {
    return {
      errorRecreated: false, // prevent endless loop, only try re-creating img src once!
      srcsetConfigSizes: {
        origin: '',
        objectDefault: '(min-width: 1400px) 350px, (min-width: 768px) 250px, (min-width: 576px) 725px, (max-width: 575px) 100vw, 100vw',
        objectGridBig: '(min-width: 576px) 725px, (max-width: 575px) 100vw, 100vw',
        objectGridSub: '100vw',
        mapObjectOverlay: '(min-width: 1200px) 200px, (min-width: 576px) 300px, (max-width: 575px) 200px, 100vw',
        thumbnail: '120px',
      },
      srcsetConfigSrcset: {
        origin: [
          {
            dimension: 'org',
            width: '320',
          },
        ],
        objectBigSlider: [
          {
            dimension: '1024x635',
            width: '1024',
          },
          {
            dimension: '575x360',
            width: '575',
          },
          {
            dimension: '725x450',
            width: '725',
          },
          {
            dimension: '250x155',
            width: '250',
          },
          {
            dimension: '350x220',
            width: '350',
          },
        ],
        // displayed in gallery-slider-custom, search results, map-overlay and objectpage object-photos
        objectDefault: [
          {
            dimension: '575x360',
            width: '575',
          },
          {
            dimension: '725x450',
            width: '725',
          },
          {
            dimension: '250x155',
            width: '250',
          },
          {
            dimension: '350x220',
            width: '350',
          },
        ],
        // only displayed on object page. the two images on the left of big object image
        // only one size, therefore it does not need sizes="blabla" prop
        objectGrid: [
          {
            dimension: '320x210',
            width: '320',
          },
        ],
        // for the big image in object top grid or objects with only one img
        objectGridBig: [
          {
            dimension: '725x450',
            width: '725',
          },
          {
            dimension: '425x320',
            width: '425',
          },
        ],
        objectGridSub: [
          {
            dimension: '575x360',
            width: '425',
          },
        ],
        thumbnail: [
          {
            dimension: '250x155',
            width: '250',
          },
        ],
        mapObjectOverlay: [
          {
            dimension: '200x200',
            width: '200',
          },
          {
            dimension: '300x300',
            width: '300',
          },
        ],
      }
    };
  },
  computed: {
    srcsetConfig() {
      return {
        default: '425x320',
        preview: `${this.previewSize}${(this.ilMixin_objectImagesWebp ? '_webp' : '')}`,
        sizes: this.srcsetConfigSizes,
        srcset: this.srcsetConfigSrcset,
      };
    },
    imgObj() {
      // build srcset and sizes string for <img> attributes
      const sizes = this.srcsetConfig.sizes[this.imgType];
      const srcset = [];
      Object.values(this.srcsetConfig.srcset[this.imgType]).forEach((srcsetData) => {
        srcset.push(`${this.imageLoader(srcsetData.dimension, `${this.imgSrc}`, this.objectNumber, this.imgCdn)} ${srcsetData.width}w`);
      });
      const srcsetWebp = [];
      if (this.ilMixin_objectImagesWebp) {
        Object.values(this.srcsetConfig.srcset[this.imgType]).forEach((srcsetData) => {
          srcsetWebp.push(`${this.imageLoader(`${srcsetData.dimension}_webp`, this.imgSrc, this.objectNumber, this.imgCdn)} ${srcsetData.width}w`);
        });
      }

      // create alt and title with description of image and imageNumber
      let alt = '';
      let title = '';
      if (!this.altOnly) {
        if (this.imgNo === 1 && this.objectTitle && this.imgTitle) { // Ferienhaus "Am Emsdeich", Bild 1: Schiffsüberführung
          alt = `${this.objectTitle}, ${this.$t('object:gallerySlider.descriptionFallback', { imageNumber: this.imgNo })}: ${this.imgTitle}`;
          title = `${this.objectTitle}, ${this.imgTitle}`;
        } else if (this.imgNo === 1 && this.objectTitle && !this.imgTitle) { // Ferienhaus "Am Emsdeich", Bild 1
          alt = `${this.objectTitle}, ${this.$t('object:gallerySlider.descriptionFallback', { imageNumber: this.imgNo })}`;
          title = alt;
        } else if (this.imgTitle) { // Bild 2: Schiffsüberführung Tag 2
          alt = `${this.$t('object:gallerySlider.descriptionFallback', { imageNumber: this.imgNo })}: ${this.imgTitle}`;
          title = this.imgTitle;
        } else { // Bild 2
          alt = this.$t('object:gallerySlider.descriptionFallback', { imageNumber: this.imgNo });
          title = alt;
        }
      } else { // if hide, dont set title, use ALT only
        title = null;
        alt = this.altOnly;
      }

      return {
        sizes,
        srcset: srcset.join(','),
        srcsetWebp: srcsetWebp.join(','),
        src: this.imageLoader(this.srcsetConfig.default, this.imgSrc, this.objectNumber, this.imgCdn),
        preview: this.imageLoader(this.srcsetConfig.preview, this.imgSrc, this.objectNumber, this.imgCdn),
        title,
        alt,
      };
    },
    objectTitle() { // is either saved in ObjectData or in SearchObject
      // Don't change order of SearchData and ObjectData! (otherwise ObjectData is showed on every SearchResult if existing)
      const searchData = this.$store.getters.getSearchObjectById(this.objectNumber);
      if (searchData.title) {
        return searchData.title;
      }


      // // initial visit on object-page
      const objectData = this.$store.getters.getObjectData();
      if (this.objectNumber && objectData.title) {
        return objectData.title;
      }

      const lastSeenObjectData = this.$store.state.searchObjectsLastSeen;
      if (lastSeenObjectData[this.objectNumber]) {
        return lastSeenObjectData[this.objectNumber].title;
      }
      return '';
    },
  },
  methods: {
    imgLoadErr(event) {
      // try to re-create img via re-loading img
      // only try once to prevent endless loop!
      if (!this.errorRecreated) {
        this.errorRecreated = true;
        if (event.target?.currentSrc) {
          const imgSizeMatch = event.target?.currentSrc.match(/_([0-9]+x[0-9]+)\.(jpg|webp)$/i);
          if (imgSizeMatch && imgSizeMatch[1]) {
            const imgSize = `${imgSizeMatch[1]}${this.ilMixin_objectImagesWebp ? '_webp' : ''}`;

            // replace img src
            const imgSrcCreated = this.imageLoader(imgSize, this.imgSrc, this.objectNumber);
            if (this.$refs.imgMain) {
              this.$refs.imgMain.srcset = imgSrcCreated;
              this.$refs.imgMain.src = imgSrcCreated;
            }
          }
        }
      }
    },
    forceLazyload() {
      if (window) {
        window.lazySizes.loader.unveil(this.$refs.imgMain);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.lazy {
  will-change: transform; // gives some performance
}
.blur {
  filter: blur(2px);
}
.gallery-grid-photo-wrapper {
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  picture {
    display: flex;
    img {
      object-fit: cover;
    }
  }
}
</style>

<style lang="scss">
  .pz-zoom-button.pz-zoom-control-position-bottom {
    display: none;
  }
</style>
